import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { QuickLoggerFactory } from '@/lib/universal-logger';

const authLogger = QuickLoggerFactory.auth();

export function AdminAuthGuard() {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAdminStatus() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (error) {
          authLogger.error("Error checking admin status:", { 
            error: error instanceof Error ? error.message : String(error)
          });
          setIsAdmin(false);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to verify admin privileges.",
          });
        } else {
          setIsAdmin(data?.is_admin || false);
        }
      } catch (error) {
        authLogger.error("Unexpected error checking admin status:", { 
          error: error instanceof Error ? error.message : String(error)
        });
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    }

    if (!authLoading) {
      checkAdminStatus();
    }
  }, [user, authLoading, toast]);

  // Still loading authentication status
  if (authLoading || loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Verifying access...</p>
      </div>
    );
  }

  // Not authenticated, redirect to login
  if (!user) {
    toast({
      title: "Authentication required",
      description: "You must be logged in to access the admin area.",
      variant: "destructive",
    });
    return <Navigate to="/auth?redirect=/admin" replace />;
  }

  // Authenticated but not admin
  if (!isAdmin) {
    toast({
      title: "Access denied",
      description: "You don't have admin privileges to access this area.",
      variant: "destructive",
    });
    return <Navigate to="/" replace />;
  }

  // Authenticated and admin
  return <Outlet />;
}
